<template>
  <div class="companies-table content-box" data-cy="companies-table">
    <ChometzCompanyDetailsModal
      v-if="showDetailsModal && detailsCompany"
      :company="detailsCompany"
      @close-modal="showDetailsModal = false"
    />
    <ConfirmationModal
      v-if="showReviewConfirmationModal"
      :text="reviewConfirmationText"
      @close-modal="onReviewConfirmationModalClosed"
    />

    <div class="companies-table-header grid-control__container table-modifiers clearfix">
      <TableSearch
        class="company-search"
        :value="searchInput"
        placeholder="Search by Company / Plant Name or ID"
        @searchSubmitted="onSearchSubmitted"
        data-cy="company-search"
      />
      <TableSearch
        class="sales-year-search"
        type="number"
        :value="selectedYear.toString()"
        :min="minYear"
        :max="maxYear"
        placeholder="Sales Year"
        @searchSubmitted="onSalesYearChanged"
      />
      <ToggleSwitch v-model="showOnlyRcCompanies" text="Show My Companies / Plants Only" />
      <div class="header-right">
        <Checkbox id="print-version" v-model="printVersion" label="Print Version" data-cy="print-version-checkbox" />

        <LoadingButton themeColor="primary" type="button" :loading="exporting" @click="exportAllExcelAsync(true)"
          >Export Filtered Data To Excel
        </LoadingButton>

        <LoadingButton themeColor="primary" type="button" @click="exportAllExcelAsync(false)" :loading="exporting"
          >Export All To Excel
        </LoadingButton>
        <ClearFiltersButton @clearFiltersClicked="clearFilters" data-cy="clear-filters" />
      </div>
    </div>
    <ChometzCompaniesGrid
      :companies="companies"
      :currentChometzSeasonSelected="selectedYear == maxYear"
      :canReverseFinalization="canReverseFinalization"
      :companiesLoading="companiesLoading"
      :pageSize="take"
      :skip="skip"
      :totalCompanies="totalCompanies"
      :filter="filter"
      :chometzSaleSeason="maxYear"
      :getPropValues="getCompanyPropValuesAsync"
      ref="gridRef"
      @companiesChanged="onCompaniesChanged"
      @sortChanged="onSortChanged"
      @detailsClicked="onDetailsClicked"
      @noChometzChanged="onNoChometzChanged"
      @reviewedChanged="onReviewedChanged"
    />
    <ColumnMenuIconHandler :filter="filter" />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import ChometzCompaniesGrid from "./ChometzCompaniesGrid.vue";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref, watch, watchEffect } from "vue";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { getJsonFilterString, valueFilterConvert } from "@/shared/helpers/filtersHelper";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import ClearFiltersButton from "@/shared/ui/inputs/ClearFiltersButton.vue";
import type { ChometzCompany } from "@/Domain/ChometzCompany";
import type { CompositeFilterDescriptor, FilterDescriptor } from "@progress/kendo-data-query";
import type { ItemChangedOptions, ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import type { GetRcChometzCompaniesWithFilteringRequest } from "@/api/chomatzSales";

import { GridOptionsService } from "@/shared/services/GridOptionsService";
import ToggleSwitch from "@/shared/ui/switch/ToggleSwitch.vue";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import { type ExcelExportColumnOptions, saveExcel } from "@progress/kendo-vue-excel-export";
import ChometzCompanyDetailsModal from "./ChometzCompanyDetailsModal.vue";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import moment from "moment";
import { setQueryParameter } from "@/shared/helpers/urlHelper";
import ConfirmationModal from "@/shared/ui/modal/ConfirmationModal.vue";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import { EnumChometzSaleResponseMode } from "@/Domain/EnumChometzSaleResponseMode";
import { Checkbox } from "@progress/kendo-vue-inputs";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import { UserRoles } from "@/Domain/UserRoles";

const main = useMainStore();
const notificationStore = useNotificationStore();
const urlParams = new URLSearchParams(window.location.search);

const searchInput = ref<string>("");
const companies = ref<ChometzCompany[]>([]);
const totalCompanies = ref<number>(0);
const companiesLoading = ref<boolean>(false);
const showOnlyRcCompanies = ref<boolean>(urlParams.get("mode")?.toLowerCase() != "all");
const exporting = ref<boolean>(false);

const showReviewConfirmationModal = ref<boolean>(false);
const reviewChometzCompany = ref<ChometzCompany | null>(null);

const filter = ref<CompositeFilterDescriptor>({
  logic: "and",
  filters: [{} as FilterDescriptor],
});
const sortField = ref<string>("");
const sortAscending = ref<boolean>(true);

const take = ref<number>(25);
const skip = ref<number>(0);

const tableName = "CompaniesChometzRcTable";

const minYear = 2000;
const maxYear = ref<number>(new Date().getFullYear());
const selectedYear = ref<number>(maxYear.value);

const showDetailsModal = ref<boolean>(false);
const detailsCompany = ref<ChometzCompany | null>(null);

const gridRef = ref<InstanceType<typeof ChometzCompaniesGrid> | null>(null);
const printVersion = ref<boolean>(false);

const canReverseFinalization = computed(() =>
  main.isInAnyRole([UserRoles.ROLE_PESACH_ADMINISTRATION, UserRoles.ROLE_MIS, UserRoles.ROLE_IN_HOUSE_MIS_MANAGER])
);

const valueFiltersConvertOptions = [
  {
    column: "SubmittedDate",
    convert: (value: string) => {
      return new Date(value).toLocaleString();
    },
    convertBack: (value: string) => {
      return moment(value).format("MM-DD-YYYY");
    },
  },
  {
    column: "ResponseMode",
    convert: (value: number) => {
      return EnumChometzSaleResponseMode[value];
    },
    convertBack: (value: string) => {
      return EnumChometzSaleResponseMode[value as keyof typeof EnumChometzSaleResponseMode];
    },
  },
] as ValueFieldConvertOption[];

const reviewConfirmationText = computed(() => {
  const isFinalizing = reviewChometzCompany.value?.ResponseFinalized;
  const companyOrPlant = reviewChometzCompany.value?.IsPlant ? "plant" : "company";
  const companyOrPlantName = reviewChometzCompany.value?.IsPlant
    ? reviewChometzCompany.value?.PlantName
    : reviewChometzCompany.value?.CompanyName;

  if (isFinalizing) {
    return `Are you sure you want to mark ${companyOrPlant} ${companyOrPlantName} as reviwed? This action cannot be undone.`;
  }

  return `Are you sure you want to reverse finalization for ${companyOrPlant} ${companyOrPlantName}?`;
});

const getChometzCompaniesWithFilteringAsync = async function (
  filterObject: CompositeFilterDescriptor | null = filter.value,
  takeValue: number = take.value,
  skipValue: number = skip.value,
  valuesForFieldOnly: string = "",
  includeAddresses: boolean = false,
  includeAttachments: boolean = true
) {
  const requestParams = {
    userLogin: main.userLogin,
    salesYear: selectedYear.value,
    onlyRcCompanies: showOnlyRcCompanies.value,
    searchValue: searchInput.value,
    filters: getJsonFilterString(filterObject, valueFiltersConvertOptions),
    skip: skipValue,
    take: takeValue,
    sortField: sortField.value,
    ascending: sortAscending.value,
    valuesForFieldOnly: valuesForFieldOnly,
    includeAddresses: includeAddresses,
    includeAttachments: includeAttachments,
  } as GetRcChometzCompaniesWithFilteringRequest;

  if (!valuesForFieldOnly) return api.get("GetRcChometzCompaniesPlantsWithFiltering", requestParams);

  requestParams.take = 1000;
  return CacheService.getAsync(createCacheKey(requestParams), () =>
    api.get("GetRcChometzCompaniesPlantsWithFiltering", requestParams)
  );
};

const clearFilters = function () {
  searchInput.value = "";
  filter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };
  sortField.value = "";
  sortAscending.value = true;
  skip.value = 0;
  getChometzCompaniesAsync();
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
  skip.value = 0;
  getChometzCompaniesAsync();
};

const onSalesYearChanged = function (year: string) {
  selectedYear.value = parseInt(year);
  skip.value = 0;
  getChometzCompaniesAsync();
};

const onCompaniesChanged = function (options: ItemChangedOptions) {
  if (options.filterEvent) {
    filter.value = options.filterEvent.filter;
  }
  if (take.value != options.take) {
    take.value = options.take;
    GridOptionsService.savePageSizeAsync(tableName, options.take);
  }
  skip.value = options.skip;
  getChometzCompaniesAsync();
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  skip.value = 0;
  getChometzCompaniesAsync();
};

const onDetailsClicked = function (company: ChometzCompany) {
  detailsCompany.value = company;
  showDetailsModal.value = true;
};

const getChometzCompaniesAsync = async function () {
  if (companiesLoading.value) return;

  companiesLoading.value = true;

  await handleResponsePromiseAsync(
    getChometzCompaniesWithFilteringAsync(),
    (data) => {
      companies.value = data.ListItems;
      totalCompanies.value = data.TotalItemsInList;
    },
    "ChometzCompaniesTable.vue",
    "getChometzCompaniesAsync"
  );

  companiesLoading.value = false;
};

const getCompanyPropValuesAsync = async function (column: GridColumnPropsExtended) {
  let result: ChometzCompany[] = [];
  await handleResponsePromiseAsync(
    getChometzCompaniesWithFilteringAsync(filter.value, take.value, skip.value, column.field),
    (data) => {
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, column.field!, result);
    },
    "CompaniesTable.vue:",
    "getCompanyPropValuesAsync"
  );
  return result;
};

// Print version - Company, Plant, Response, Address, East of NY, Primary Contact, Contact Email, Contact Phone, Response Customer Name columns
const printVersionColumns = [
  "CompanyName",
  "PlantName",
  "SubmissionComment",
  "PlantFormattedAddress",
  "TimezoneEastOfNY",
  "ContactFullName",
  "ContactEmail",
  "ContactPhone",
  "SubmissionCustomerName",
];

const exportGetFormattedColumns = function () {
  if (!gridRef.value) return [];
  let cols = [...gridRef.value.columns] as ExcelExportColumnOptions[];
  cols.push({
    field: "PlantFormattedAddress",
    title: "Address",
    width: 300,
    cellOptions: {
      wrap: true,
    },
  });
  cols.push({
    field: "TimezoneEastOfNY",
    title: "East of NY",
  });
  if (printVersion.value) {
    cols = cols
      .filter((col) => printVersionColumns.includes(col.field ?? ""))
      .sort((a, b) => {
        return printVersionColumns.indexOf(a.field ?? "") - printVersionColumns.indexOf(b.field ?? "");
      });
  }
  return cols;
};

const exportAllExcelAsync = async function (filtered: boolean = false) {
  try {
    exporting.value = true;
    const data = await getChometzCompaniesWithFilteringAsync(
      filtered ? filter.value : null,
      999999,
      0,
      "",
      true,
      false
    );
    if (!data.success) {
      notificationStore.notify({
        message: "Error while exporting data. Please try again.",
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
      return;
    }
    saveExcel({
      data: data.response.ListItems,
      fileName: "ChometzCompanies",
      columns: exportGetFormattedColumns(),
    });
  } finally {
    exporting.value = false;
  }
};

const onNoChometzChanged = async function (company: ChometzCompany) {
  const noChometzValue = company.IsNoChometz;
  const submitPromise = api.post("SetNoChometz", {
    CompanyId: !company.IsPlant ? company.CompanyId : 0,
    PlantId: company.IsPlant ? company.PlantId : 0,
    NoChometz: noChometzValue,
  });

  let companyPlantMessage = `company ${company.CompanyName}`;
  if (company.IsPlant) {
    companyPlantMessage = `plant ${company.PlantName} of company ${company.CompanyName}`;
  }

  const showErrorNotification = () => {
    notificationStore.notify({
      message: `Error while changing No Chometz status for ${companyPlantMessage}. Please try again.`,
      type: NotificationTypeEnum.error,
      duration: 5000,
    });
  };

  await handleResponsePromiseAsync(
    submitPromise,
    (data) => {
      if (data) {
        notificationStore.notify({
          message: `No Chometz status changed for ${companyPlantMessage} successfully.`,
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
        if (noChometzValue) {
          company.ResponseFinalized = false;
          company.SubmissionCustomerName = "";
          company.SubmissionComment = "";
          company.SubmittedDate = null;
          company.Submitted = false;
          company.Timezone = "";
        }
        company.ResponseFinalized = false;
      } else {
        showErrorNotification();
        company.IsNoChometz = !noChometzValue;
      }
    },
    "ChometzCompaniesTable.vue",
    "onNoChometzChanged",
    () => {
      showErrorNotification();
      company.IsNoChometz = !noChometzValue;
    }
  );
};

const onReviewedChanged = function (company: ChometzCompany) {
  reviewChometzCompany.value = company;
  showReviewConfirmationModal.value = true;
};

const onReviewConfirmationModalClosed = function (confirmed: boolean) {
  if (reviewChometzCompany.value) {
    if (confirmed) {
      onReviewConfirmed(reviewChometzCompany.value);
    } else {
      reviewChometzCompany.value!.ResponseFinalized = !reviewChometzCompany.value!.ResponseFinalized;
    }
  }
  showReviewConfirmationModal.value = false;
};

const onReviewConfirmed = async function (company: ChometzCompany) {
  const responseFinalized = company.ResponseFinalized;
  const submitPromise = api.post("ReviewChometzSaleResponse", {
    CompanyId: !company.IsPlant ? company.CompanyId : 0,
    PlantId: company.IsPlant ? company.PlantId : 0,
    ResponseFinalized: responseFinalized,
  });

  let companyPlantMessage = `Company ${company.CompanyName}`;
  if (company.IsPlant) {
    companyPlantMessage = `Plant ${company.PlantName} of company ${company.CompanyName}`;
  }

  const showErrorNotification = () => {
    notificationStore.notify({
      message: `Error while ${
        responseFinalized ? "setting reviewed status for" : "reversing finalization for"
      } ${companyPlantMessage}. Please try again.`,
      type: NotificationTypeEnum.error,
      duration: 5000,
    });
  };

  await handleResponsePromiseAsync(
    submitPromise,
    (data) => {
      if (data) {
        notificationStore.notify({
          message: `${
            responseFinalized ? "Reviewed status set for" : "Finalization reversed for"
          } ${companyPlantMessage} successfully.`,
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
      } else {
        showErrorNotification();
        company.ResponseFinalized = !responseFinalized;
      }
    },
    "ChometzCompaniesTable.vue",
    "onReviewedChanged",
    () => {
      showErrorNotification();
      company.ResponseFinalized = !responseFinalized;
    }
  );
};

const getCurrentChometzSaleSeasonAsync = async function () {
  return handleResponsePromiseAsync(
    CacheService.getResponseAsync("GetCurrentChometzSaleSeason", () => api.get("GetCurrentChometzSaleSeason", {})),
    (data) => {
      maxYear.value = data;
      selectedYear.value = data;
    },
    "ChometzCompaniesTable.vue",
    "getCurrentChometzSaleSeasonAsync"
  );
};

watchEffect(() => {
  console.debug("CompaniesTable.vue, searchInput watch updated:", searchInput.value);
  main.companyRcFilterCriteria = searchInput.value;
  setQueryParameter("search", searchInput.value);
});

watch(
  () => [showOnlyRcCompanies.value],
  () => {
    skip.value = 0;
    getChometzCompaniesAsync();
    urlParams.set("mode", showOnlyRcCompanies.value ? "my" : "all");
    window.history.pushState({}, "", `${window.location.pathname}?${urlParams}`);
  }
);

onBeforeMount(async () => {
  await getCurrentChometzSaleSeasonAsync();
  await getChometzCompaniesAsync();
});
</script>

<style lang="scss">
.companies-table {
  .k-checkbox-wrap {
    align-self: unset;
  }
}

.companies-table-header {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.header-right {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
  flex-wrap: wrap;
}

.company-search {
  width: 400px;
}

.sales-year-search {
  width: 150px;
}
</style>
